import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId, useState } from 'react';

export const CONTROL =
  'w-full rounded-field border border-[rgb(223,228,235)] bg-white px-3.5 text-sm font-normal text-[rgb(20,32,51)] outline-none transition focus:border-royal/55 focus:ring-[3px] focus:ring-royal/15 dark:border-dark-line dark:bg-dark-field dark:text-dark-ink';

export const CONTROL_FIELD = `h-11 ${CONTROL} py-0`;

export const CONTROL_SELECT = `h-11 ${CONTROL} flex items-center gap-2 py-0 text-left`;

export const COMPACT_CONTROL =
  'h-9 w-full rounded-field border border-hairline bg-white px-3 text-[13.5px] text-navy outline-none focus:border-royal/55 dark:border-dark-line dark:bg-dark-field dark:text-dark-ink';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-field font-bold transition disabled:cursor-not-allowed disabled:opacity-50';

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-[34px] px-3 text-[13px]',
  md: 'min-h-[40px] px-4 text-[14px]',
  lg: 'min-h-[44px] px-5 text-[15px]',
};

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-gold text-navy shadow-gold hover:brightness-[1.03] hover:shadow-gold-lift active:translate-y-px',
  secondary:
    'border border-hairline bg-white text-navy hover:bg-mist hover:-translate-y-px dark:border-dark-line dark:bg-dark-panel dark:text-dark-ink',
  ghost: 'text-navy hover:bg-navy/5 dark:text-dark-ink dark:hover:bg-white/5',
  danger: 'bg-critical text-white hover:brightness-110',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, className = '', children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={`${BUTTON_BASE} ${BUTTON_SIZES[size]} ${BUTTON_VARIANTS[variant]} ${className}`}
      disabled={rest.disabled === true || loading}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
});

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
}

function EyeIcon({ crossed }: { readonly crossed: boolean }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-[18px]"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.8" />
      {crossed ? <path d="M4 20 20 4" /> : null}
    </svg>
  );
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, hint, className = '', type = 'text', ...rest },
  ref,
) {
  const id = useId();
  const [revealed, setRevealed] = useState(false);
  const describedBy = error !== undefined ? `${id}-error` : hint !== undefined ? `${id}-hint` : undefined;

  const isPassword = type === 'password';

  return (
    <div className="mb-4">
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <div className="relative">
        <input
          ref={ref}
          id={id}
          type={isPassword && revealed ? 'text' : type}
          aria-invalid={error !== undefined}
          {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
          className={`w-full rounded-field border-[1.5px] bg-field px-3.5 py-2.5 text-[15px] text-navy transition outline-none placeholder:text-muted/70 focus:border-royal/55 focus:bg-white focus:ring-[3px] focus:ring-royal/15 dark:bg-dark-field dark:text-dark-ink ${
            isPassword ? 'pr-12' : ''
          } ${error === undefined ? 'border-navy/20 dark:border-dark-line' : 'border-critical/60'} ${className}`}
          {...rest}
        />
        {isPassword ? (
          <button
            type="button"
            onClick={() => {
              setRevealed((current) => !current);
            }}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            className="absolute inset-y-0 right-0 grid w-11 place-items-center rounded-r-field text-muted transition hover:text-navy focus-visible:ring-[3px] focus-visible:ring-royal/15 focus-visible:outline-none dark:hover:text-dark-ink"
          >
            <EyeIcon crossed={revealed} />
          </button>
        ) : null}
      </div>
      {error !== undefined ? (
        <p id={`${id}-error`} role="alert" className="mt-2 text-[12.5px] text-critical">
          {error}
        </p>
      ) : hint !== undefined ? (
        <p id={`${id}-hint`} className="mt-2 text-[12.5px] text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

export function Spinner({ className = '' }: { readonly className?: string }): ReactNode {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={`inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    />
  );
}

interface AlertProps {
  readonly tone: 'error' | 'success' | 'info';
  readonly children: ReactNode;
}

const ALERT_TONES: Record<AlertProps['tone'], string> = {
  error: 'border-critical/25 bg-critical-soft text-critical',
  success: 'border-positive/25 bg-positive-soft text-positive',
  info: 'border-royal/25 bg-royal/6 text-navy dark:text-dark-ink',
};

export function Alert({ tone, children }: AlertProps): ReactNode {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`mb-4 rounded-field border px-3.5 py-3 text-[14px] leading-snug ${ALERT_TONES[tone]}`}
    >
      {children}
    </div>
  );
}

interface BadgeProps {
  readonly tone?: 'neutral' | 'gold' | 'cyan' | 'positive' | 'caution' | 'critical';
  readonly children: ReactNode;
}

const BADGE_TONES: Record<NonNullable<BadgeProps['tone']>, string> = {
  neutral: 'bg-navy/6 text-navy dark:bg-white/10 dark:text-dark-ink',
  gold: 'bg-gold/25 text-[#6a4b00]',
  cyan: 'bg-cyan/20 text-[#0a4a66]',
  positive: 'bg-positive-soft text-positive',
  caution: 'bg-caution-soft text-caution',
  critical: 'bg-critical-soft text-critical',
};

export function Badge({ tone = 'neutral', children }: BadgeProps): ReactNode {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-[0.02em] ${BADGE_TONES[tone]}`}
    >
      {children}
    </span>
  );
}
